import React, { useMemo } from "react";
import PropTypes from "prop-types";
import { useHistory } from "react-router-dom";
import {
  Card,
  Box,
  Typography,
  Stack,
  Avatar,
  Divider,
} from "@mui/material";
import {
  SportsEsportsOutlined as MineIcon,
  ScheduleOutlined as ClockIcon,
  ArrowForward as ArrowForwardIcon,
  Verified as VerifiedIcon,
  LocalFireDepartment as FireIcon,
} from "@mui/icons-material";
import moment from "moment";
import { colors, borderRadiusPx, customShadows } from "../../../../../theme";
import { DEFAULT_CURRENCY } from "../../../../../static/Constants";
import { safeFromWei } from "../../../../../static/utils";
import getImageUrlByHash from "../../../../../static/logoUtils";
import { CommonButton, CommonChip } from "../../../../Components/ui";
import MineGridPreview from "./MineGridPreview";

const MineLotteryCard = ({
  lottery,
  partner,
  partnerLogo,
  currency = DEFAULT_CURRENCY,
}) => {
  const history = useHistory();

  const lotteryId = lottery?.lotteryId;
  const statusId = lottery?.status;
  const isRollover =
    statusId === "3" ||
    statusId === "5" ||
    Boolean(lottery?.rolloverperct && Number(lottery.rolloverperct) > 0);

  const totalPrizeFormatted = useMemo(
    () => safeFromWei(lottery?.totalPrize),
    [lottery?.totalPrize]
  );

  const entryFeeFormatted = useMemo(
    () => safeFromWei(lottery?.entryFee),
    [lottery?.entryFee]
  );

  const capacity = Number(lottery?.capacity) || 20;

  const deployedDateFormatted = useMemo(() => {
    if (!lottery?.deployedOn) return null;
    return moment.unix(Number(lottery.deployedOn)).format("DD MMM YYYY");
  }, [lottery?.deployedOn]);

  const partnerLogoUrl = useMemo(() => {
    if (!partner?.logoHash) return null;
    return getImageUrlByHash(partner.logoHash, partnerLogo);
  }, [partner?.logoHash, partnerLogo]);

  const partnerDisplayName = useMemo(() => {
    if (partner?.name && partner.name.trim()) return partner.name;
    if (partner?.companyName && partner.companyName.trim()) return partner.companyName;
    const addr = lottery?.partnerAddress || lottery?.partner || "";
    if (addr && addr.length > 10) {
      return `${addr.slice(0, 6)}...${addr.slice(-4)}`;
    }
    return "Verified Creator";
  }, [partner, lottery]);

  const handlePlayNow = () => {
    history.push(`/minelottery/${lotteryId}`);
  };

  return (
    <Card
      className="mine-lottery-card"
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        bgcolor: colors.dashboard.cardBg,
        borderRadius: borderRadiusPx.xl,
        border: `1px solid ${colors.dashboard.cardBorder}`,
        boxShadow: customShadows.cardFlat,
        position: "relative",
        overflow: "hidden",
        transition: "all 0.25s ease",
        "&:hover": {
          borderColor: colors.purple[400],
          boxShadow: "0 12px 28px rgba(124, 58, 237, 0.12)",
          transform: "translateY(-4px)",
        },
      }}
    >
      {/* Top Accent Gradient Bar */}
      <Box
        sx={{
          height: 4,
          background: "linear-gradient(135deg, #a855f7 0%, #7c3aed 100%)",
        }}
      />

      <Box sx={{ p: 2.75, flexGrow: 1, display: "flex", flexDirection: "column" }}>
        {/* Top Badges Row */}
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          spacing={1}
          sx={{ mb: 2 }}
        >
          {/* Game Type Badge */}
          <Stack direction="row" spacing={1} alignItems="center">
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: borderRadiusPx.md,
                bgcolor: colors.purple[50],
                color: colors.purple[600],
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <MineIcon sx={{ fontSize: 18 }} />
            </Box>
            <div>
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 700,
                  color: colors.purple[700],
                  lineHeight: 1.1,
                  fontSize: "0.85rem",
                }}
              >
                Autobet Mine #{lotteryId}
              </Typography>
              {deployedDateFormatted && (
                <Typography
                  variant="caption"
                  sx={{
                    color: colors.slate[400],
                    display: "flex",
                    alignItems: "center",
                    gap: 0.4,
                    fontSize: "0.7rem",
                  }}
                >
                  <ClockIcon sx={{ fontSize: 11 }} />
                  {deployedDateFormatted}
                </Typography>
              )}
            </div>
          </Stack>

          {/* Status Badge */}
          {isRollover ? (
            <CommonChip
              icon={<FireIcon sx={{ fontSize: 13 }} />}
              label="Rollover Open"
              variant="rollover"
              size="small"
            />
          ) : (
            <CommonChip
              label="Open"
              variant="active"
              size="small"
            />
          )}
        </Stack>

        {/* Creator / Host Row */}
        <Stack
          direction="row"
          alignItems="center"
          spacing={1.25}
          sx={{
            py: 1.25,
            px: 1.5,
            bgcolor: colors.slate[50],
            borderRadius: borderRadiusPx.md,
            border: `1px solid ${colors.slate[200]}`,
            mb: 2,
          }}
        >
          <Avatar
            src={partnerLogoUrl || undefined}
            alt={partnerDisplayName}
            sx={{
              width: 28,
              height: 28,
              bgcolor: colors.purple[100],
              color: colors.purple[700],
              fontSize: "0.75rem",
              fontWeight: 700,
            }}
          >
            {partnerDisplayName.charAt(0).toUpperCase()}
          </Avatar>
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <Typography
                variant="caption"
                noWrap
                sx={{
                  color: colors.slate[800],
                  fontWeight: 700,
                  fontSize: "0.78rem",
                }}
              >
                {partnerDisplayName}
              </Typography>
              <VerifiedIcon sx={{ fontSize: 13, color: colors.purple[600] }} />
            </Stack>
            <Typography
              variant="caption"
              sx={{ color: colors.slate[400], fontSize: "0.68rem" }}
            >
              Minefield Creator
            </Typography>
          </Box>
        </Stack>

        {/* Prize Pool Display */}
        <Box
          sx={{
            p: 2,
            mb: 1.5,
            borderRadius: borderRadiusPx.lg,
            bgcolor: colors.purple[50],
            border: `1px solid ${colors.purple[200]}`,
            textAlign: "center",
          }}
        >
          <Typography
            variant="caption"
            sx={{
              color: colors.purple[700],
              fontWeight: 700,
              textTransform: "uppercase",
              letterSpacing: 0.8,
              fontSize: "0.72rem",
            }}
          >
            Chance to Win
          </Typography>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 800,
              color: colors.purple[900],
              my: 0.25,
              fontSize: "1.75rem",
            }}
          >
            {totalPrizeFormatted}{" "}
            <Typography
              component="span"
              sx={{ fontSize: "1.1rem", fontWeight: 700, color: colors.purple[700] }}
            >
              {currency}
            </Typography>
          </Typography>
          <Typography
            variant="caption"
            sx={{ color: colors.purple[600], fontWeight: 500, fontSize: "0.72rem" }}
          >
            On-Chain Winner Takes All
          </Typography>
        </Box>

        {/* Mini Minefield Matrix Visualizer */}
        <MineGridPreview capacity={capacity} maxPreviewTiles={12} />

        {/* Specs Row */}
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{
            py: 1.25,
            px: 1,
            mt: "auto",
            mb: 2,
            borderTop: `1px dashed ${colors.slate[200]}`,
          }}
        >
          {/* Entry Fee */}
          <Box>
            <Typography
              variant="caption"
              sx={{
                color: colors.slate[400],
                display: "block",
                fontSize: "0.7rem",
                fontWeight: 600,
              }}
            >
              ENTRY FEE
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: colors.slate[900], fontWeight: 700 }}
            >
              {entryFeeFormatted} {currency}
            </Typography>
          </Box>

          <Divider orientation="vertical" flexItem sx={{ borderColor: colors.slate[200] }} />

          {/* Slots / Capacity */}
          <Box sx={{ textAlign: "right" }}>
            <Typography
              variant="caption"
              sx={{
                color: colors.slate[400],
                display: "block",
                fontSize: "0.7rem",
                fontWeight: 600,
              }}
            >
              GRID CAPACITY
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: colors.purple[700], fontWeight: 700 }}
            >
              {capacity} Slots (Pick 1)
            </Typography>
          </Box>
        </Stack>

        {/* Play Now CTA Button */}
        <CommonButton
          variant="purpleSolid"
          size="medium"
          fullWidth
          onClick={handlePlayNow}
          endIcon={<ArrowForwardIcon sx={{ fontSize: 18 }} />}
          label="Play Mine Now!"
        />
      </Box>
    </Card>
  );
};

MineLotteryCard.propTypes = {
  lottery: PropTypes.object.isRequired,
  partner: PropTypes.object,
  partnerLogo: PropTypes.array,
  currency: PropTypes.string,
};

export default MineLotteryCard;
